import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';

const formatDateOnly = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCount = (value) => Number(value || 0).toLocaleString();

const getDataSetLabel = (dataSet) => (
  `${dataSet?.data_source || 'Unknown'} ${dataSet?.dataset_type || 'Unknown'} FY${dataSet?.fiscal_year || '—'}`
);

const getBundleLabel = (bundle) => (
  `${bundle?.data_set?.data_source || 'Unknown'} ${bundle?.data_set?.dataset_type || 'Unknown'} FY${bundle?.data_set?.fiscal_year || '—'}`
);

const getDefinitionLabel = (definition) => (
  `${definition?.data_source || 'Unknown'} ${definition?.dataset_type || 'Unknown'}`
);

const formatWarning = (warning) => {
  if (typeof warning === 'string') return warning;
  if (!warning) return '';
  return warning.message || warning.summary || JSON.stringify(warning);
};

const getAreaProfileCachePolicyLabel = (policy) => {
  switch (policy) {
    case 'included':
      return 'Included';
    case 'omitted_rebuild_required':
      return 'Omitted; rebuild required';
    default:
      return policy || 'Not specified';
  }
};

const getStatusLabel = (value) => value || 'not reported';

const getReconSummary = (recon) => {
  if (!recon || typeof recon === 'string') return null;
  const sourceValidation = recon.source_format_validation || {};
  const excelValidation = sourceValidation.excel || {};
  const excelCompleteness = excelValidation.limit_completeness || {};
  const pdfValidation = sourceValidation.pdf || {};
  const ct = recon.ct_injection || {};
  const prior = recon.prior_bundle_comparison || {};
  const areas = prior.areas || {};
  const mappingChanges = prior.location_mapping_changes || {};
  const metrics = prior.press_release_metrics || {};
  const repair = recon.location_name_repair || {};

  return {
    sourceValidation: {
      excelStatus: excelValidation.status,
      excelMissingColumns: (excelValidation.missing_required_columns || []).length,
      excelMissingLimitColumns: (excelValidation.missing_limit_columns || []).length,
      excelCompletenessStatus: excelCompleteness.status,
      excelIncompleteAreas: excelCompleteness.incomplete_area_count,
      pdfStatus: pdfValidation.status,
      pdfMissingRows: (pdfValidation.missing_state_or_country_rows || []).length,
      pdfIncompleteAreas: pdfValidation.incomplete_area_count,
      pdfRequiredLimitTypes: pdfValidation.required_limit_types || [],
    },
    ct: {
      enabled: ct.enabled,
      locations: ct.rows_added?.locations,
      locationCounties: ct.rows_added?.location_counties,
      unmapped: (ct.unmapped_towns || []).length,
    },
    changes: {
      addedAreas: (areas.all_added_names || areas.added_names || []).length,
      removedAreas: (areas.all_removed_names || areas.removed_names || []).length,
      changedMappings: mappingChanges.changed_location_area_mapping_count,
      countyMappingDelta: metrics.county_mapping_delta,
      locationCountDelta: metrics.location_count_delta,
    },
    repair: {
      mode: repair.mode,
      safe: repair.safe_repair_count,
      applied: repair.applied_repair_count,
      ambiguous: repair.ambiguous_count,
      unmatched: repair.unmatched_suspect_count,
    },
  };
};

const inferStateAbbrFromBundle = (bundle) => {
  const rows = bundle?.records?.location_counties || bundle?.records?.locations || [];
  const unique = new Set((Array.isArray(rows) ? rows : [])
    .map((row) => String(row.state_abbr || '').trim().toUpperCase())
    .filter(Boolean));
  return unique.size === 1 ? Array.from(unique)[0] : '';
};

const inferStateCodeFromBundle = (bundle) => {
  const rows = bundle?.records?.location_counties || bundle?.records?.locations || [];
  const unique = new Set((Array.isArray(rows) ? rows : [])
    .map((row) => String(row.state_code || '').trim().padStart(2, '0'))
    .filter(Boolean));
  return unique.size === 1 ? Array.from(unique)[0] : '';
};

const buildImportDefinitionDraft = (preview, bundle, fallbackCategories = []) => {
  const options = preview?.definition?.entitlement_category_options || fallbackCategories || [];
  const firstCategory = options[0] || '';
  return {
    source_agency_name: preview?.definition?.source_agency_name || preview?.data_set?.source_agency_name || '',
    source_program_name: preview?.definition?.source_program_name || preview?.data_set?.source_program_name || '',
    entitlement_category: firstCategory,
    entitlement_mode: firstCategory ? 'existing' : 'new',
    new_entitlement_category: '',
    geo_scope_type: inferStateAbbrFromBundle(bundle) ? 'STATE' : 'NATIONAL',
    state_abbr: inferStateAbbrFromBundle(bundle),
    state_code: inferStateCodeFromBundle(bundle),
  };
};

const TABLE_WRAPPER_STYLE = {
  overflowX: 'auto',
};

const OpsDataSetManagement = () => {
  const [dataSets, setDataSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [activeTab, setActiveTab] = useState('catalog');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [filters, setFilters] = useState({
    data_source: '',
    dataset_type: '',
    fiscal_year: '',
  });
  const [editingDataSet, setEditingDataSet] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [endDateDraft, setEndDateDraft] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [pendingImport, setPendingImport] = useState(null);
  const [catalog, setCatalog] = useState({ definitions: [], aliases: [], default_rules: [], entitlement_categories: [] });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [aliasModal, setAliasModal] = useState(null);
  const [geographyModal, setGeographyModal] = useState(null);
  const [definitionModal, setDefinitionModal] = useState(null);
  const [aliasDraft, setAliasDraft] = useState({
    alias_data_source: '',
    alias_dataset_type: '',
    canonical_data_source: '',
    canonical_dataset_type: '',
    description: '',
  });
  const [geographyDraft, setGeographyDraft] = useState({
    state_abbr: '',
    state_code: '',
  });
  const [definitionDraft, setDefinitionDraft] = useState({
    entitlement_mode: 'existing',
    entitlement_category: '',
    new_entitlement_category: '',
    geo_scope_type: 'STATE',
    states: [],
    new_state_abbr: '',
    aliases: [],
  });
  const [importDefinitionDraft, setImportDefinitionDraft] = useState({
    source_agency_name: '',
    source_program_name: '',
    entitlement_category: '',
    entitlement_mode: 'existing',
    new_entitlement_category: '',
    geo_scope_type: 'STATE',
    state_abbr: '',
    state_code: '',
  });
  const importFileRef = useRef(null);

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      const response = await api.get('/acct-mgmt/v1/support/il-data-set-catalog');
      const data = response.data || { definitions: [], aliases: [], default_rules: [], entitlement_categories: [], states: [] };
      setCatalog(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data set catalog.');
      return null;
    } finally {
      setCatalogLoading(false);
    }
  };

  const loadDataSets = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        include_archived: includeArchived ? 'true' : 'false',
      };
      Object.entries(filters).forEach(([key, value]) => {
        const normalized = String(value || '').trim();
        if (normalized) params[key] = normalized;
      });
      const response = await api.get('/acct-mgmt/v1/support/il-data-sets', { params });
      setDataSets(response.data?.data_sets || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load income limit data sets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDataSets();
    loadCatalog();
  }, [includeArchived]);

  const filterOptions = useMemo(() => {
    const sources = new Set();
    const types = new Set();
    const fiscalYears = new Set();
    dataSets.forEach((dataSet) => {
      if (dataSet.data_source) sources.add(dataSet.data_source);
      if (dataSet.dataset_type) types.add(dataSet.dataset_type);
      if (dataSet.fiscal_year) fiscalYears.add(String(dataSet.fiscal_year));
    });
    return {
      sources: Array.from(sources).sort(),
      types: Array.from(types).sort(),
      fiscalYears: Array.from(fiscalYears).sort((a, b) => Number(b) - Number(a)),
    };
  }, [dataSets]);

  const openDescriptionModal = (dataSet) => {
    setEditingDataSet(dataSet);
    setDescriptionDraft(dataSet.description || '');
    setEndDateDraft(toDateInputValue(dataSet.end_date));
  };

  const saveDescription = async () => {
    if (!editingDataSet) return;
    const actionKey = `description-${editingDataSet.data_set_id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.patch(`/acct-mgmt/v1/support/il-data-sets/${editingDataSet.data_set_id}`, {
        description: descriptionDraft,
        end_date: endDateDraft || null,
      });
      setEditingDataSet(null);
      setDescriptionDraft('');
      setEndDateDraft('');
      setStatus({ type: 'success', message: response.data?.message || 'Metadata updated.' });
      await loadDataSets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update description.');
    } finally {
      setActionLoading('');
    }
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    const { dataSet, action } = pendingAction;
    const actionKey = `${action}-${dataSet.data_set_id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const actionPath = action === 'cache' ? 'cache/rebuild' : action;
      const response = await api.post(`/acct-mgmt/v1/support/il-data-sets/${dataSet.data_set_id}/${actionPath}`);
      setPendingAction(null);
      setStatus({ type: 'success', message: response.data?.message || 'Income limit data set updated.' });
      await loadDataSets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update income limit data set.');
    } finally {
      setActionLoading('');
    }
  };

  const downloadExport = async (dataSet) => {
    const actionKey = `export-${dataSet.data_set_id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.get(`/acct-mgmt/v1/support/il-data-sets/${dataSet.data_set_id}/export`, {
        responseType: 'blob',
      });
      const contentDisposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `il-data-set-${dataSet.data_set_id}.json`;
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setStatus({ type: 'success', message: 'Income limit data set exported.' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export income limit data set.');
    } finally {
      setActionLoading('');
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = '';
    if (!file) return;
    setStatus({ type: '', message: '' });
    setError('');
    setActionLoading('import-preview');
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      try {
        const response = await api.post('/acct-mgmt/v1/support/il-data-sets/import/preview', bundle);
        const preview = response.data?.preview;
        setPendingImport({ bundle, filename: file.name, preview });
        setImportDefinitionDraft(buildImportDefinitionDraft(preview, bundle, catalog.entitlement_categories));
      } catch (previewErr) {
        const preview = previewErr.response?.data?.preview;
        if (preview) {
          setPendingImport({ bundle, filename: file.name, preview });
          setImportDefinitionDraft(buildImportDefinitionDraft(preview, bundle, catalog.entitlement_categories));
        } else {
          throw previewErr;
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to preview import file.');
    } finally {
      setActionLoading('');
    }
  };

  const confirmImport = async () => {
    if (!pendingImport) return;
    setActionLoading('import');
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const needsDefinition = pendingImport.preview?.definition?.missing;
      const response = await api.post('/acct-mgmt/v1/support/il-data-sets/import', needsDefinition ? {
        bundle: pendingImport.bundle,
        definition: {
          data_source: pendingImport.preview?.data_set?.data_source,
          dataset_type: pendingImport.preview?.data_set?.dataset_type,
          source_agency_name: importDefinitionDraft.source_agency_name,
          source_program_name: importDefinitionDraft.source_program_name,
          entitlement_category: importDefinitionDraft.entitlement_mode === 'new'
            ? importDefinitionDraft.new_entitlement_category
            : importDefinitionDraft.entitlement_category,
          geo_scope_type: importDefinitionDraft.geo_scope_type,
          state_abbr: importDefinitionDraft.geo_scope_type === 'STATE' ? importDefinitionDraft.state_abbr : null,
          state_code: importDefinitionDraft.geo_scope_type === 'STATE' ? importDefinitionDraft.state_code : null,
        },
      } : pendingImport.bundle);
      setPendingImport(null);
      setStatus({ type: 'success', message: response.data?.message || 'Income limit data set imported.' });
      await loadDataSets();
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import income limit data set.');
    } finally {
      setActionLoading('');
    }
  };

  const openAliasModal = (definition, alias = null) => {
    setAliasModal({ definition, alias });
    setAliasDraft({
      alias_data_source: alias?.alias_data_source || '',
      alias_dataset_type: alias?.alias_dataset_type || '',
      canonical_data_source: definition?.data_source || alias?.canonical_data_source || '',
      canonical_dataset_type: definition?.dataset_type || alias?.canonical_dataset_type || '',
      description: alias?.description || '',
    });
  };

  const saveAlias = async () => {
    if (!aliasModal) return;
    const actionKey = aliasModal.alias ? `alias-${aliasModal.alias.id}` : 'alias-create';
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = aliasModal.alias
        ? await api.patch(`/acct-mgmt/v1/support/il-data-set-catalog/aliases/${aliasModal.alias.id}`, {
            alias_data_source: aliasDraft.alias_data_source,
            alias_dataset_type: aliasDraft.alias_dataset_type,
            description: aliasDraft.description,
          })
        : await api.post('/acct-mgmt/v1/support/il-data-set-catalog/aliases', aliasDraft);
      setAliasModal(null);
      setStatus({ type: 'success', message: response.data?.message || 'Alias saved.' });
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save alias.');
    } finally {
      setActionLoading('');
    }
  };

  const deactivateAlias = async (alias) => {
    const actionKey = `alias-delete-${alias.id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.delete(`/acct-mgmt/v1/support/il-data-set-catalog/aliases/${alias.id}`);
      setStatus({ type: 'success', message: response.data?.message || 'Alias deactivated.' });
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate alias.');
    } finally {
      setActionLoading('');
    }
  };

  const openGeographyModal = (definition) => {
    setGeographyModal({ definition });
    setGeographyDraft({ state_abbr: '', state_code: '' });
  };

  const addGeography = async () => {
    if (!geographyModal?.definition) return;
    const actionKey = `geo-create-${geographyModal.definition.id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/support/il-data-set-catalog/geographies', {
        data_source: geographyModal.definition.data_source,
        dataset_type: geographyModal.definition.dataset_type,
        state_abbr: geographyDraft.state_abbr,
        state_code: geographyDraft.state_code,
      });
      setGeographyModal(null);
      setStatus({ type: 'success', message: response.data?.message || 'Geography added.' });
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add geography.');
    } finally {
      setActionLoading('');
    }
  };

  const removeGeography = async (geo) => {
    const actionKey = `geo-delete-${geo.id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.delete(`/acct-mgmt/v1/support/il-data-set-catalog/geographies/${geo.id}`);
      setStatus({ type: 'success', message: response.data?.message || 'Geography removed.' });
      await loadCatalog();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove geography.');
    } finally {
      setActionLoading('');
    }
  };

  const buildDefinitionDraft = (definition, sourceCatalog = catalog) => {
    const entitlementOptions = sourceCatalog.entitlement_categories || [];
    const hasExistingEntitlement = entitlementOptions.includes(definition?.entitlement_category);
    const stateRows = (definition?.geography || []).filter((geo) => geo.geo_scope_type === 'STATE');
    return {
      entitlement_mode: hasExistingEntitlement ? 'existing' : 'new',
      entitlement_category: hasExistingEntitlement ? definition.entitlement_category : (entitlementOptions[0] || ''),
      new_entitlement_category: hasExistingEntitlement ? '' : (definition?.entitlement_category || ''),
      geo_scope_type: (definition?.geography || []).some((geo) => geo.geo_scope_type === 'NATIONAL') ? 'NATIONAL' : 'STATE',
      states: stateRows.map((geo) => ({
        id: geo.id,
        state_code: geo.state_code,
        state_abbr: geo.state_abbr,
      })),
      new_state_abbr: '',
      aliases: (definition?.aliases || []).map((alias) => ({ ...alias })),
    };
  };

  const openDefinitionModal = (definition) => {
    setDefinitionModal({ definition });
    setDefinitionDraft(buildDefinitionDraft(definition));
  };

  const refreshDefinitionModal = async (definitionId = definitionModal?.definition?.id) => {
    const refreshed = await loadCatalog();
    if (!refreshed || !definitionId) return null;
    const definition = (refreshed.definitions || []).find((row) => row.id === definitionId);
    if (definition) {
      setDefinitionModal({ definition });
      setDefinitionDraft(buildDefinitionDraft(definition, refreshed));
    }
    return definition;
  };

  const saveDefinitionEntitlement = async () => {
    if (!definitionModal?.definition) return;
    const actionKey = `definition-entitlement-${definitionModal.definition.id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const entitlementCategory = definitionDraft.entitlement_mode === 'new'
        ? definitionDraft.new_entitlement_category
        : definitionDraft.entitlement_category;
      const response = await api.patch(`/acct-mgmt/v1/support/il-data-set-catalog/definitions/${definitionModal.definition.id}`, {
        entitlement_category: entitlementCategory,
      });
      setStatus({ type: 'success', message: response.data?.message || 'Definition updated.' });
      await refreshDefinitionModal(definitionModal.definition.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update definition.');
    } finally {
      setActionLoading('');
    }
  };

  const saveDefinitionGeography = async () => {
    if (!definitionModal?.definition) return;
    const actionKey = `definition-geography-${definitionModal.definition.id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = await api.put(`/acct-mgmt/v1/support/il-data-set-catalog/definitions/${definitionModal.definition.id}/geography`, {
        geo_scope_type: definitionDraft.geo_scope_type,
        states: definitionDraft.geo_scope_type === 'STATE' ? definitionDraft.states : [],
      });
      setStatus({ type: 'success', message: response.data?.message || 'Geography updated.' });
      await refreshDefinitionModal(definitionModal.definition.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update geography.');
    } finally {
      setActionLoading('');
    }
  };

  const addDefinitionState = () => {
    const selected = (catalog.states || []).find((state) => state.state_abbr === definitionDraft.new_state_abbr);
    if (!selected) return;
    setDefinitionDraft((prev) => {
      if ((prev.states || []).some((state) => state.state_code === selected.state_code)) return prev;
      return {
        ...prev,
        states: [
          ...(prev.states || []),
          {
            state_code: selected.state_code,
            state_abbr: selected.state_abbr,
          },
        ].sort((a, b) => String(a.state_abbr).localeCompare(String(b.state_abbr))),
        new_state_abbr: '',
      };
    });
  };

  const removeDefinitionState = (stateCode) => {
    setDefinitionDraft((prev) => ({
      ...prev,
      states: (prev.states || []).filter((state) => state.state_code !== stateCode),
    }));
  };

  const updateAliasDraftRow = (index, field, value) => {
    setDefinitionDraft((prev) => ({
      ...prev,
      aliases: (prev.aliases || []).map((alias, aliasIndex) => (
        aliasIndex === index ? { ...alias, [field]: value } : alias
      )),
    }));
  };

  const addAliasDraftRow = () => {
    if (!definitionModal?.definition) return;
    setDefinitionDraft((prev) => ({
      ...prev,
      aliases: [
        ...(prev.aliases || []),
        {
          id: `new-${Date.now()}`,
          alias_data_source: '',
          alias_dataset_type: '',
          canonical_data_source: definitionModal.definition.data_source,
          canonical_dataset_type: definitionModal.definition.dataset_type,
          description: '',
          active_flag: true,
          is_new: true,
        },
      ],
    }));
  };

  const saveDefinitionAlias = async (alias) => {
    if (!definitionModal?.definition) return;
    const actionKey = `definition-alias-${alias.id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const payload = {
        alias_data_source: alias.alias_data_source,
        alias_dataset_type: alias.alias_dataset_type,
        canonical_data_source: definitionModal.definition.data_source,
        canonical_dataset_type: definitionModal.definition.dataset_type,
        description: alias.description,
      };
      const response = alias.is_new
        ? await api.post('/acct-mgmt/v1/support/il-data-set-catalog/aliases', payload)
        : await api.patch(`/acct-mgmt/v1/support/il-data-set-catalog/aliases/${alias.id}`, payload);
      setStatus({ type: 'success', message: response.data?.message || 'Alias saved.' });
      await refreshDefinitionModal(definitionModal.definition.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save alias.');
    } finally {
      setActionLoading('');
    }
  };

  const setDefinitionAliasActive = async (alias, active) => {
    if (!definitionModal?.definition || alias.is_new) return;
    const actionKey = `definition-alias-active-${alias.id}`;
    setActionLoading(actionKey);
    setStatus({ type: '', message: '' });
    setError('');
    try {
      const response = active
        ? await api.post(`/acct-mgmt/v1/support/il-data-set-catalog/aliases/${alias.id}/reactivate`)
        : await api.delete(`/acct-mgmt/v1/support/il-data-set-catalog/aliases/${alias.id}`);
      setStatus({ type: 'success', message: response.data?.message || 'Alias updated.' });
      await refreshDefinitionModal(definitionModal.definition.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update alias.');
    } finally {
      setActionLoading('');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ data_source: '', dataset_type: '', fiscal_year: '' });
  };

  const actionCopy = pendingAction ? {
    release: {
      title: 'Release data set?',
      button: 'Release',
      className: 'btn-primary',
      body: `${getDataSetLabel(pendingAction.dataSet)} will become available to service API requests when it is the matching selected data set.`,
    },
    unrelease: {
      title: 'Unrelease data set?',
      button: 'Unrelease',
      className: 'btn-warning',
      body: `${getDataSetLabel(pendingAction.dataSet)} will no longer be returned by service API dataset selection or customer-facing dataset listing.`,
    },
    archive: {
      title: 'Archive data set?',
      button: 'Archive',
      className: 'btn-danger',
      body: `${getDataSetLabel(pendingAction.dataSet)} will be removed from active service API use and hidden from the default ops list. This is the supported V1 cleanup path and is not restored from this screen.`,
    },
    cache: {
      title: 'Rebuild data set cache?',
      button: 'Rebuild Cache',
      className: 'btn-primary',
      body: `Area profile JSON will be regenerated for ${getDataSetLabel(pendingAction.dataSet)}. This can update cached service API response data for every area in the data set.`,
    },
  }[pendingAction.action] : null;
  const importReconSummary = getReconSummary(pendingImport?.preview?.recon);
  const importDefinitionReady = !pendingImport?.preview?.definition?.missing || (
    (importDefinitionDraft.entitlement_mode === 'new'
      ? String(importDefinitionDraft.new_entitlement_category || '').trim()
      : String(importDefinitionDraft.entitlement_category || '').trim())
    && (importDefinitionDraft.geo_scope_type !== 'STATE'
      || String(importDefinitionDraft.state_abbr || importDefinitionDraft.state_code || '').trim())
  );
  const refreshActiveTab = () => {
    if (activeTab === 'catalog') {
      loadCatalog();
    } else {
      loadDataSets();
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Data Set Management</h2>
          <div className="text-muted">Manage service API data-set catalog rules and income-limit data-set lifecycle.</div>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={refreshActiveTab}
          disabled={loading || catalogLoading || Boolean(actionLoading)}
        >
          {(loading || catalogLoading) ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {status.message && <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>}
      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'catalog' ? 'active' : ''}`}
            onClick={() => setActiveTab('catalog')}
          >
            Catalog
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'data-sets' ? 'active' : ''}`}
            onClick={() => setActiveTab('data-sets')}
          >
            Income Limit Data Sets
          </button>
        </li>
      </ul>

      {activeTab === 'catalog' && (
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between w-100 mb-3 border-bottom pb-2">
            <div>
              <h5 className="mb-0">Data Set Catalog</h5>
              <div className="small text-muted">View canonical definitions, aliases, geography, and defaulting rules used by service API dataset resolution.</div>
            </div>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={loadCatalog} disabled={catalogLoading || Boolean(actionLoading)}>
              {catalogLoading ? 'Refreshing...' : 'Refresh Catalog'}
            </button>
          </div>

          {catalogLoading ? (
            <div className="text-muted">Loading data set catalog...</div>
          ) : (
            <>
              <div className="table-responsive mb-4" style={TABLE_WRAPPER_STYLE}>
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Definition</th>
                      <th>Agency / Program</th>
                      <th>Entitlement</th>
                      <th>Geography</th>
                      <th>Aliases</th>
                      <th>Data Sets</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(catalog.definitions || []).map((definition) => (
                      <tr key={definition.id}>
                        <td className="fw-semibold">{getDefinitionLabel(definition)}</td>
                        <td style={{ minWidth: '16rem' }}>
                          <div>{definition.source_agency_name || <span className="text-muted">No agency</span>}</div>
                          <div className="small text-muted">{definition.source_program_name || 'No program'}</div>
                        </td>
                        <td><span className="badge bg-secondary">{definition.entitlement_category}</span></td>
                        <td>
                          {(definition.geography || []).length === 0 ? (
                            <span className="text-muted">Not defined</span>
                          ) : (definition.geography || []).map((geo) => (
                            <span key={geo.id} className="badge bg-light text-dark border me-1 mb-1">
                              {geo.geo_scope_type === 'NATIONAL' ? 'National' : geo.state_abbr}
                            </span>
                          ))}
                        </td>
                        <td style={{ minWidth: '18rem' }}>
                          {(definition.aliases || []).length === 0 ? (
                            <span className="text-muted">No aliases</span>
                          ) : (definition.aliases || []).map((alias) => (
                            <div key={alias.id} className="d-flex align-items-center gap-2 mb-1">
                              <span className={`badge ${alias.active_flag ? 'bg-info text-dark' : 'bg-secondary'}`}>
                                {alias.alias_data_source} {alias.alias_dataset_type}
                              </span>
                              {!alias.active_flag && <span className="small text-muted">Inactive</span>}
                            </div>
                          ))}
                        </td>
                        <td>
                          <div>Total: {formatCount(definition.data_set_count)}</div>
                          <div className="small text-muted">Released: {formatCount(definition.released_data_set_count)}</div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => openDefinitionModal(definition)}
                            disabled={Boolean(actionLoading)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border rounded p-3 bg-light">
                <div className="fw-semibold mb-2">Defaulting Rules</div>
                {(catalog.default_rules || []).length === 0 ? (
                  <div className="small text-muted">No defaulting rules defined.</div>
                ) : (
                  <div className="row g-2">
                    {(catalog.default_rules || []).map((rule) => (
                      <div key={rule.id} className="col-md-6">
                        <div className="small">
                          <span className="fw-semibold">{rule.data_source}</span>: {rule.rule_type === 'STATIC_DATASET_TYPE'
                            ? `defaults to ${rule.static_dataset_type}`
                            : `uses pattern ${rule.dataset_type_pattern}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      )}

      {activeTab === 'data-sets' && (
      <div className="card mb-4">
        <div className="card-body">
          {actionLoading === 'import-preview' && (
            <div className="alert alert-info d-flex align-items-center gap-2" role="alert">
              <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
              <span>Validating import bundle...</span>
            </div>
          )}
          <div className="d-flex align-items-center justify-content-between w-100 mb-3 border-bottom pb-2">
            <h5 className="mb-0">Income Limit Data</h5>
            <div className="d-flex align-items-center gap-3">
              <input
                ref={importFileRef}
                type="file"
                className="d-none"
                accept="application/json,.json"
                onChange={handleImportFile}
              />
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => importFileRef.current?.click()}
                disabled={loading || Boolean(actionLoading)}
              >
                <i className="fas fa-file-import me-1" aria-hidden="true"></i>
                Import JSON
              </button>
              <div className="form-check form-switch finbase-toggle mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="include-archived-data-sets"
                  checked={includeArchived}
                  onChange={(event) => setIncludeArchived(event.target.checked)}
                />
                <label className="form-check-label" htmlFor="include-archived-data-sets">
                  Include archived
                </label>
              </div>
            </div>
          </div>

          <div className="row g-3 align-items-end mb-3">
            <div className="col-md-3">
              <label className="form-label">Data Source</label>
              <select className="form-select" value={filters.data_source} onChange={(event) => handleFilterChange('data_source', event.target.value)}>
                <option value="">All sources</option>
                {filterOptions.sources.map((source) => <option key={source} value={source}>{source}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Data Set Type</label>
              <select className="form-select" value={filters.dataset_type} onChange={(event) => handleFilterChange('dataset_type', event.target.value)}>
                <option value="">All types</option>
                {filterOptions.types.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Fiscal Year</label>
              <select className="form-select" value={filters.fiscal_year} onChange={(event) => handleFilterChange('fiscal_year', event.target.value)}>
                <option value="">All years</option>
                {filterOptions.fiscalYears.map((year) => <option key={year} value={year}>{year}</option>)}
              </select>
            </div>
            <div className="col-md-3 d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={loadDataSets} disabled={loading || Boolean(actionLoading)}>
                Apply
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={clearFilters} disabled={loading || Boolean(actionLoading)}>
                Clear
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-muted">Loading income limit data sets...</div>
          ) : dataSets.length === 0 ? (
            <div className="text-muted">No income limit data sets found.</div>
          ) : (
            <div className="table-responsive" style={TABLE_WRAPPER_STYLE}>
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Data Set</th>
                    <th>Dates</th>
                    <th>Description</th>
                    <th>Records</th>
                    <th>Cache</th>
                    <th>State</th>
                    <th>Loaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dataSets.map((dataSet) => {
                    const archived = Boolean(dataSet.archived_at);
                    return (
                      <tr key={dataSet.data_set_id} className={archived ? 'table-secondary' : ''}>
                        <td>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-semibold">{dataSet.data_source} {dataSet.dataset_type}</span>
                            {dataSet.is_latest && !archived && (
                              <span className="badge bg-info text-dark">Latest Available</span>
                            )}
                          </div>
                          <div className="small text-muted">FY{dataSet.fiscal_year}</div>
                        </td>
                        <td>
                          <div>Effective: {formatDateOnly(dataSet.start_date)}</div>
                          <div className="small text-muted">End: {formatDateOnly(dataSet.end_date)}</div>
                        </td>
                        <td style={{ minWidth: '16rem' }}>
                          <div>{dataSet.description || <span className="text-muted">No description</span>}</div>
                        </td>
                        <td>
                          <div>Areas: {formatCount(dataSet.area_count)}</div>
                          <div className="small text-muted">Locations: {formatCount(dataSet.location_count)}</div>
                          <div className="small text-muted">Limits: {formatCount(dataSet.limit_count)}</div>
                          <div className="small text-muted">County Maps: {formatCount(dataSet.county_mapping_count)}</div>
                        </td>
                        <td>
                          <div>
                            <span className={`badge ${
                              Number(dataSet.area_count || 0) > 0 && Number(dataSet.area_profile_uncached_count || 0) === 0
                                ? 'bg-success'
                                : Number(dataSet.area_profile_cached_count || 0) > 0
                                  ? 'bg-warning text-dark'
                                  : 'bg-secondary'
                            }`}>
                              {Number(dataSet.area_count || 0) > 0 && Number(dataSet.area_profile_uncached_count || 0) === 0
                                ? 'Complete'
                                : Number(dataSet.area_profile_cached_count || 0) > 0
                                  ? 'Partial'
                                  : 'Missing'}
                            </span>
                          </div>
                          <div className="small text-muted mt-1">
                            {formatCount(dataSet.area_profile_cached_count)} / {formatCount(dataSet.area_count)} area profiles
                          </div>
                        </td>
                        <td>
                          <div>
                            <span className={`badge ${dataSet.is_released && !archived ? 'bg-success' : 'bg-secondary'}`}>
                              {dataSet.is_released && !archived ? 'Released' : 'Unreleased'}
                            </span>
                          </div>
                          {archived && (
                            <div className="mt-1">
                              <span className="badge bg-dark">Archived</span>
                            </div>
                          )}
                          {archived && (
                            <div className="small text-muted mt-1">
                              {formatDateTime(dataSet.archived_at)}
                            </div>
                          )}
                        </td>
                        <td>{formatDateTime(dataSet.loaded_date)}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              title="Edit description"
                              onClick={() => openDescriptionModal(dataSet)}
                              disabled={Boolean(actionLoading)}
                            >
                              <i className="fas fa-pen" aria-hidden="true"></i>
                            </button>
                            {!archived && dataSet.is_released && (
                              <button
                                type="button"
                                className="btn btn-outline-warning btn-sm"
                                title="Unrelease data set"
                                onClick={() => setPendingAction({ dataSet, action: 'unrelease' })}
                                disabled={Boolean(actionLoading)}
                              >
                                <i className="fas fa-eye-slash" aria-hidden="true"></i>
                              </button>
                            )}
                            {!archived && !dataSet.is_released && (
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                title="Release data set"
                                onClick={() => setPendingAction({ dataSet, action: 'release' })}
                                disabled={Boolean(actionLoading)}
                              >
                                <i className="fas fa-eye" aria-hidden="true"></i>
                              </button>
                            )}
                            {!archived && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                title="Archive data set"
                                onClick={() => setPendingAction({ dataSet, action: 'archive' })}
                                disabled={Boolean(actionLoading)}
                              >
                                <i className="fas fa-archive" aria-hidden="true"></i>
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              title="Export data set JSON"
                              onClick={() => downloadExport(dataSet)}
                              disabled={Boolean(actionLoading)}
                            >
                              <i className="fas fa-file-export" aria-hidden="true"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              title="Rebuild area profile cache"
                              onClick={() => setPendingAction({ dataSet, action: 'cache' })}
                              disabled={Boolean(actionLoading)}
                            >
                              <i className="fas fa-sync-alt" aria-hidden="true"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {editingDataSet && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit data set metadata</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setEditingDataSet(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="fw-semibold">{getDataSetLabel(editingDataSet)}</div>
                    <div className="small text-muted">Effective {formatDateOnly(editingDataSet.start_date)}</div>
                  </div>
                  <label className="form-label" htmlFor="data-set-description">Description</label>
                  <textarea
                    id="data-set-description"
                    className="form-control"
                    rows="4"
                    value={descriptionDraft}
                    onChange={(event) => setDescriptionDraft(event.target.value)}
                  />
                  <label className="form-label mt-3" htmlFor="data-set-end-date">End Date</label>
                  <input
                    id="data-set-end-date"
                    className="form-control"
                    type="date"
                    value={endDateDraft}
                    onChange={(event) => setEndDateDraft(event.target.value)}
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditingDataSet(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveDescription}
                    disabled={actionLoading === `description-${editingDataSet.data_set_id}`}
                  >
                    {actionLoading === `description-${editingDataSet.data_set_id}` ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {pendingAction && actionCopy && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{actionCopy.title}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setPendingAction(null)}></button>
                </div>
                <div className="modal-body">
                  <p>{actionCopy.body}</p>
                  {pendingAction.action === 'archive' && (
                    <div className="alert alert-warning mb-0" role="alert">
                      Archive is a one-way lifecycle change from this ops screen. The data stays in the database, but it will no longer be active for service API use.
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setPendingAction(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn ${actionCopy.className}`}
                    onClick={runPendingAction}
                    disabled={actionLoading === `${pendingAction.action}-${pendingAction.dataSet.data_set_id}`}
                  >
                    {actionLoading === `${pendingAction.action}-${pendingAction.dataSet.data_set_id}` ? 'Working...' : actionCopy.button}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {definitionModal && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title">Edit catalog definition</h5>
                    <div className="small text-muted">{getDefinitionLabel(definitionModal.definition)}</div>
                  </div>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setDefinitionModal(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-4">
                    <div className="fw-semibold mb-2">Entitlement</div>
                    <div className="row g-3 align-items-end">
                      <div className="col-md-4">
                        <label className="form-label" htmlFor="definition-entitlement-mode">Mode</label>
                        <select
                          id="definition-entitlement-mode"
                          className="form-select"
                          value={definitionDraft.entitlement_mode}
                          onChange={(event) => setDefinitionDraft((prev) => ({ ...prev, entitlement_mode: event.target.value }))}
                        >
                          <option value="existing">Existing category</option>
                          <option value="new">New category</option>
                        </select>
                      </div>
                      <div className="col-md-5">
                        {definitionDraft.entitlement_mode === 'existing' ? (
                          <>
                            <label className="form-label" htmlFor="definition-entitlement-category">Category</label>
                            <select
                              id="definition-entitlement-category"
                              className="form-select"
                              value={definitionDraft.entitlement_category}
                              onChange={(event) => setDefinitionDraft((prev) => ({ ...prev, entitlement_category: event.target.value }))}
                            >
                              {(catalog.entitlement_categories || []).map((category) => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </select>
                          </>
                        ) : (
                          <>
                            <label className="form-label" htmlFor="definition-new-entitlement-category">New Category</label>
                            <input
                              id="definition-new-entitlement-category"
                              className="form-control"
                              value={definitionDraft.new_entitlement_category}
                              onChange={(event) => setDefinitionDraft((prev) => ({ ...prev, new_entitlement_category: event.target.value }))}
                            />
                          </>
                        )}
                      </div>
                      <div className="col-md-3">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          onClick={saveDefinitionEntitlement}
                          disabled={Boolean(actionLoading)}
                        >
                          {actionLoading.startsWith('definition-entitlement') ? 'Saving...' : 'Save Entitlement'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 border-top pt-3">
                    <div className="fw-semibold mb-2">Geography</div>
                    <div className="row g-3 align-items-end mb-3">
                      <div className="col-md-4">
                        <label className="form-label" htmlFor="definition-geo-scope">Scope</label>
                        <select
                          id="definition-geo-scope"
                          className="form-select"
                          value={definitionDraft.geo_scope_type}
                          onChange={(event) => setDefinitionDraft((prev) => ({ ...prev, geo_scope_type: event.target.value }))}
                        >
                          <option value="NATIONAL">National</option>
                          <option value="STATE">State-scoped</option>
                        </select>
                      </div>
                      <div className="col-md-8">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={saveDefinitionGeography}
                          disabled={Boolean(actionLoading) || (definitionDraft.geo_scope_type === 'STATE' && (definitionDraft.states || []).length === 0)}
                        >
                          {actionLoading.startsWith('definition-geography') ? 'Saving...' : 'Save Geography'}
                        </button>
                      </div>
                    </div>
                    {definitionDraft.geo_scope_type === 'STATE' && (
                      <>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {(definitionDraft.states || []).length === 0 ? (
                            <span className="text-muted small">No states selected.</span>
                          ) : (definitionDraft.states || []).map((state) => (
                            <span key={state.state_code} className="badge bg-light text-dark border">
                              {state.state_abbr}
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-danger p-0 ms-1 align-baseline"
                                onClick={() => removeDefinitionState(state.state_code)}
                                aria-label={`Remove ${state.state_abbr}`}
                              >
                                x
                              </button>
                            </span>
                          ))}
                        </div>
                        <div className="row g-3 align-items-end">
                          <div className="col-md-8">
                            <label className="form-label" htmlFor="definition-add-state">Add State</label>
                            <select
                              id="definition-add-state"
                              className="form-select"
                              value={definitionDraft.new_state_abbr}
                              onChange={(event) => setDefinitionDraft((prev) => ({ ...prev, new_state_abbr: event.target.value }))}
                            >
                              <option value="">Select state...</option>
                              {(catalog.states || []).map((state) => (
                                <option key={state.state_code} value={state.state_abbr}>
                                  {state.state_abbr} - {state.state_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4">
                            <button
                              type="button"
                              className="btn btn-outline-primary w-100"
                              onClick={addDefinitionState}
                              disabled={!definitionDraft.new_state_abbr}
                            >
                              Add State
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="border-top pt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="fw-semibold">Aliases</div>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={addAliasDraftRow}>
                        Add Alias
                      </button>
                    </div>
                    {(definitionDraft.aliases || []).length === 0 ? (
                      <div className="text-muted small">No aliases defined.</div>
                    ) : (
                      <div className="table-responsive" style={TABLE_WRAPPER_STYLE}>
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Status</th>
                              <th>Alias Source</th>
                              <th>Alias Type</th>
                              <th>Description</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(definitionDraft.aliases || []).map((alias, index) => (
                              <tr key={alias.id}>
                                <td>
                                  <span className={`badge ${alias.active_flag ? 'bg-success' : 'bg-secondary'}`}>
                                    {alias.active_flag ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  <input
                                    className="form-control form-control-sm"
                                    value={alias.alias_data_source}
                                    onChange={(event) => updateAliasDraftRow(index, 'alias_data_source', event.target.value)}
                                  />
                                </td>
                                <td>
                                  <input
                                    className="form-control form-control-sm"
                                    value={alias.alias_dataset_type}
                                    onChange={(event) => updateAliasDraftRow(index, 'alias_dataset_type', event.target.value)}
                                  />
                                </td>
                                <td>
                                  <input
                                    className="form-control form-control-sm"
                                    value={alias.description || ''}
                                    onChange={(event) => updateAliasDraftRow(index, 'description', event.target.value)}
                                  />
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => saveDefinitionAlias(alias)}
                                      disabled={Boolean(actionLoading)}
                                    >
                                      Save
                                    </button>
                                    {!alias.is_new && alias.active_flag && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => setDefinitionAliasActive(alias, false)}
                                        disabled={Boolean(actionLoading)}
                                      >
                                        Deactivate
                                      </button>
                                    )}
                                    {!alias.is_new && !alias.active_flag && (
                                      <button
                                        type="button"
                                        className="btn btn-outline-success btn-sm"
                                        onClick={() => setDefinitionAliasActive(alias, true)}
                                        disabled={Boolean(actionLoading)}
                                      >
                                        Reactivate
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setDefinitionModal(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {aliasModal && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{aliasModal.alias ? 'Edit alias' : 'Add alias'}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setAliasModal(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="fw-semibold">Canonical: {aliasDraft.canonical_data_source} {aliasDraft.canonical_dataset_type}</div>
                    <div className="small text-muted">Aliases resolve to this canonical data set definition before entitlement checks and dataset selection.</div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="alias-data-source">Alias Data Source</label>
                      <input
                        id="alias-data-source"
                        className="form-control"
                        value={aliasDraft.alias_data_source}
                        disabled={Boolean(aliasModal.alias)}
                        onChange={(event) => setAliasDraft((prev) => ({ ...prev, alias_data_source: event.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="alias-dataset-type">Alias Data Set Type</label>
                      <input
                        id="alias-dataset-type"
                        className="form-control"
                        value={aliasDraft.alias_dataset_type}
                        disabled={Boolean(aliasModal.alias)}
                        onChange={(event) => setAliasDraft((prev) => ({ ...prev, alias_dataset_type: event.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label" htmlFor="alias-description">Description</label>
                      <textarea
                        id="alias-description"
                        className="form-control"
                        rows="3"
                        value={aliasDraft.description}
                        onChange={(event) => setAliasDraft((prev) => ({ ...prev, description: event.target.value }))}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setAliasModal(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={saveAlias} disabled={Boolean(actionLoading)}>
                    {actionLoading.startsWith('alias') ? 'Saving...' : 'Save Alias'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {geographyModal && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add state geography</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setGeographyModal(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="fw-semibold">Definition: {getDefinitionLabel(geographyModal.definition)}</div>
                    <div className="small text-muted">State geography controls which non-national catalog definitions appear in state-filtered data-set discovery.</div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="geo-state-abbr">State Abbr</label>
                      <input
                        id="geo-state-abbr"
                        className="form-control"
                        value={geographyDraft.state_abbr}
                        onChange={(event) => setGeographyDraft((prev) => ({ ...prev, state_abbr: event.target.value.toUpperCase() }))}
                        placeholder="NJ"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label" htmlFor="geo-state-code">State Code</label>
                      <input
                        id="geo-state-code"
                        className="form-control"
                        value={geographyDraft.state_code}
                        onChange={(event) => setGeographyDraft((prev) => ({ ...prev, state_code: event.target.value }))}
                        placeholder="34"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setGeographyModal(null)}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={addGeography} disabled={Boolean(actionLoading)}>
                    {actionLoading.startsWith('geo-create') ? 'Adding...' : 'Add State'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {pendingImport && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Import data set?</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setPendingImport(null)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="fw-semibold">{getBundleLabel(pendingImport.preview || pendingImport.bundle)}</div>
                    <div className="small text-muted">Effective {formatDateOnly(pendingImport.preview?.data_set?.start_date || pendingImport.bundle?.data_set?.start_date)}</div>
                    <div className="small text-muted">File: {pendingImport.filename}</div>
                  </div>
                  {pendingImport.preview?.valid ? (
                    <div className="alert alert-success" role="alert">
                      Import validation passed.
                    </div>
                  ) : (
                    <div className="alert alert-danger" role="alert">
                      Import validation found blocking issues.
                    </div>
                  )}
                  <div className="row g-2">
                    <div className="col-6">Areas: {formatCount(pendingImport.preview?.counts?.areas)}</div>
                    <div className="col-6">Locations: {formatCount(pendingImport.preview?.counts?.locations)}</div>
                    <div className="col-6">County Maps: {formatCount(pendingImport.preview?.counts?.location_counties)}</div>
                    <div className="col-6">Limits: {formatCount(pendingImport.preview?.counts?.area_limits)}</div>
                  </div>
                  {pendingImport.preview?.definition && (
                    <div className={`alert mt-3 mb-0 ${pendingImport.preview.definition.missing ? 'alert-warning' : 'alert-secondary'}`} role="alert">
                      {pendingImport.preview.definition.missing ? (
                        <span>No active catalog definition exists for this source/type. Define it before import.</span>
                      ) : (
                        <span>Catalog definition matched: {pendingImport.preview.definition.data_source} {pendingImport.preview.definition.dataset_type} ({pendingImport.preview.definition.entitlement_category})</span>
                      )}
                    </div>
                  )}
                  {pendingImport.preview?.definition?.missing && (
                    <div className="border rounded p-3 mt-3">
                      <div className="fw-semibold mb-2">Create Data Set Definition</div>
                      <div className="small text-muted mb-3">
                        The definition controls canonical source/type, entitlement grouping, geography, and alias resolution for this imported data set.
                      </div>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Data Source</label>
                          <input className="form-control" value={pendingImport.preview?.data_set?.data_source || ''} readOnly />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Data Set Type</label>
                          <input className="form-control" value={pendingImport.preview?.data_set?.dataset_type || ''} readOnly />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="import-source-agency">Source Agency Name</label>
                          <input
                            id="import-source-agency"
                            className="form-control"
                            value={importDefinitionDraft.source_agency_name}
                            onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, source_agency_name: event.target.value }))}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label" htmlFor="import-source-program">Source Program Name</label>
                          <input
                            id="import-source-program"
                            className="form-control"
                            value={importDefinitionDraft.source_program_name}
                            onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, source_program_name: event.target.value }))}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label" htmlFor="import-entitlement-mode">Entitlement Category</label>
                          <select
                            id="import-entitlement-mode"
                            className="form-select"
                            value={importDefinitionDraft.entitlement_mode}
                            onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, entitlement_mode: event.target.value }))}
                          >
                            <option value="existing">Use existing</option>
                            <option value="new">Create new</option>
                          </select>
                        </div>
                        <div className="col-md-8">
                          <label className="form-label" htmlFor="import-entitlement-category">Category Value</label>
                          {importDefinitionDraft.entitlement_mode === 'existing' ? (
                            <select
                              id="import-entitlement-category"
                              className="form-select"
                              value={importDefinitionDraft.entitlement_category}
                              onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, entitlement_category: event.target.value }))}
                            >
                              {(pendingImport.preview?.definition?.entitlement_category_options || catalog.entitlement_categories || []).map((category) => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              id="import-entitlement-category"
                              className="form-control"
                              value={importDefinitionDraft.new_entitlement_category}
                              onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, new_entitlement_category: event.target.value }))}
                              placeholder="New entitlement category"
                            />
                          )}
                        </div>
                        <div className="col-md-4">
                          <label className="form-label" htmlFor="import-geo-scope">Geography Scope</label>
                          <select
                            id="import-geo-scope"
                            className="form-select"
                            value={importDefinitionDraft.geo_scope_type}
                            onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, geo_scope_type: event.target.value }))}
                          >
                            <option value="NATIONAL">National</option>
                            <option value="STATE">State</option>
                          </select>
                        </div>
                        {importDefinitionDraft.geo_scope_type === 'STATE' && (
                          <>
                            <div className="col-md-4">
                              <label className="form-label" htmlFor="import-state-abbr">State Abbr</label>
                              <input
                                id="import-state-abbr"
                                className="form-control"
                                value={importDefinitionDraft.state_abbr}
                                onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, state_abbr: event.target.value.toUpperCase() }))}
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label" htmlFor="import-state-code">State Code</label>
                              <input
                                id="import-state-code"
                                className="form-control"
                                value={importDefinitionDraft.state_code}
                                onChange={(event) => setImportDefinitionDraft((prev) => ({ ...prev, state_code: event.target.value }))}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {pendingImport.preview?.clash?.has_active_clash && (
                    <div className="alert alert-danger mt-3 mb-0" role="alert">
                      Matching non-archived data set already exists: {pendingImport.preview.clash.active_data_set_id}
                    </div>
                  )}
                  {pendingImport.preview?.clash?.archived_match_count > 0 && !pendingImport.preview?.clash?.has_active_clash && (
                    <div className="alert alert-secondary mt-3 mb-0" role="alert">
                      Archived matching data sets found: {pendingImport.preview.clash.archived_match_count}. Import is allowed.
                    </div>
                  )}
                  {(pendingImport.preview?.blocking_errors || []).length > 0 && (
                    <div className="mt-3">
                      <div className="fw-semibold mb-1">Blocking Issues</div>
                      <ul className="mb-0">
                        {pendingImport.preview.blocking_errors.map((item, index) => (
                          <li key={`${item}-${index}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(pendingImport.preview?.warnings || []).length > 0 && (
                    <div className="mt-3">
                      <div className="fw-semibold mb-1">Warnings</div>
                      <ul className="mb-0">
                        {pendingImport.preview.warnings.map((item, index) => (
                          <li key={`${formatWarning(item)}-${index}`}>{formatWarning(item)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pendingImport.preview?.source_files?.length > 0 && (
                    <div className="mt-3">
                      <div className="fw-semibold mb-1">Source Files</div>
                      <ul className="mb-0">
                        {pendingImport.preview.source_files.map((item, index) => (
                          <li key={`source-file-${index}`}>{item.name || item.path || formatWarning(item)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pendingImport.preview?.cache_policy && (
                    <div className="mt-3">
                      <div className="fw-semibold mb-1">Cache Policy</div>
                      <div className="small">
                        Area profile JSON: {getAreaProfileCachePolicyLabel(pendingImport.preview.cache_policy.area_profile_json)}
                      </div>
                    </div>
                  )}
                  {importReconSummary && (
                    <div className="mt-3">
                      <div className="fw-semibold mb-1">Recon Summary</div>
                      <div className="border rounded p-3 bg-light">
                        <div className="row g-3 small">
                          <div className="col-md-6">
                            <div className="fw-semibold mb-1">Source Format</div>
                            <div>Excel validation: {getStatusLabel(importReconSummary.sourceValidation.excelStatus)}</div>
                            <div>Excel missing required columns: {formatCount(importReconSummary.sourceValidation.excelMissingColumns)}</div>
                            <div>Excel missing limit columns: {formatCount(importReconSummary.sourceValidation.excelMissingLimitColumns)}</div>
                            <div>Excel limit completeness: {getStatusLabel(importReconSummary.sourceValidation.excelCompletenessStatus)}</div>
                            <div>Excel incomplete areas: {formatCount(importReconSummary.sourceValidation.excelIncompleteAreas)}</div>
                            <div className="mt-2">PDF validation: {getStatusLabel(importReconSummary.sourceValidation.pdfStatus)}</div>
                            <div>PDF missing state/country rows: {formatCount(importReconSummary.sourceValidation.pdfMissingRows)}</div>
                            <div>PDF incomplete areas: {formatCount(importReconSummary.sourceValidation.pdfIncompleteAreas)}</div>
                            <div>PDF required limit types: {importReconSummary.sourceValidation.pdfRequiredLimitTypes.join(', ') || 'not reported'}</div>
                          </div>
                          <div className="col-md-6">
                            <div className="fw-semibold mb-1">Annual Changes</div>
                            <div>Added income areas: {formatCount(importReconSummary.changes.addedAreas)}</div>
                            <div>Removed income areas: {formatCount(importReconSummary.changes.removedAreas)}</div>
                            <div>Changed location mappings: {formatCount(importReconSummary.changes.changedMappings)}</div>
                            <div>County mapping delta: {formatCount(importReconSummary.changes.countyMappingDelta)}</div>
                            <div>Location count delta: {formatCount(importReconSummary.changes.locationCountDelta)}</div>
                            <div className="mt-2 fw-semibold">Connecticut Enrichment</div>
                            <div>Status: {importReconSummary.ct.enabled ? 'Included' : 'Skipped'}</div>
                            <div>Location rows added: {formatCount(importReconSummary.ct.locations)}</div>
                            <div>County mapping rows added: {formatCount(importReconSummary.ct.locationCounties)}</div>
                            <div>Unmapped towns: {formatCount(importReconSummary.ct.unmapped)}</div>
                          </div>
                          {(importReconSummary.repair.mode || importReconSummary.repair.safe || importReconSummary.repair.applied) && (
                            <div className="col-12">
                              <div className="fw-semibold mb-1">Location Name Repair</div>
                              <div className="row g-2">
                                <div className="col-sm-3">Mode: {importReconSummary.repair.mode || 'not reported'}</div>
                                <div className="col-sm-3">Safe: {formatCount(importReconSummary.repair.safe)}</div>
                                <div className="col-sm-3">Applied: {formatCount(importReconSummary.repair.applied)}</div>
                                <div className="col-sm-3">Ambiguous: {formatCount(importReconSummary.repair.ambiguous)}</div>
                                <div className="col-sm-3">Unmatched: {formatCount(importReconSummary.repair.unmatched)}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {pendingImport.preview?.recon && !importReconSummary && (
                    <div className="mt-3">
                      <div className="fw-semibold mb-1">Recon Summary</div>
                      <div className="small bg-light border rounded p-2">
                        {String(pendingImport.preview.recon).slice(0, 1000)}
                      </div>
                    </div>
                  )}
                  <div className="alert alert-info mt-3 mb-0">
                    Imported data sets are created unreleased. They will not be available to service API users until released.
                  </div>
                  {actionLoading === 'import' && (
                    <div className="d-flex align-items-center gap-2 mt-3 text-muted">
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>Importing data set rows...</span>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setPendingImport(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmImport}
                    disabled={actionLoading === 'import' || !pendingImport.preview?.valid || !importDefinitionReady}
                  >
                    {actionLoading === 'import' ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Importing...
                      </>
                    ) : 'Import'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OpsDataSetManagement;
